/**
 * PLP Nosto content badges (top-left on product cards).
 * Mirrors live `nosto-plp-script`: products tagged `modal-bundle-offer` get the
 * Nosto-hosted badge image on `.product-card__media`.
 *
 * Cards are often hydrated async (Nosto / Shopify PLP), so we re-run
 * on DOM mutations and after a short settle delay.
 */
const BADGE_URL =
  'https://d21m4dsqdd3b9h.cloudfront.net/shopify-24797970513/onsiteContent/17791831782640.6126406386932988';
const TAG = 'modal-bundle-offer';
const CACHE_LIMIT = 250;
const badgeCache = new Map();

/** @type {number | undefined} */
let injectTimer;

/**
 * @param {string | null} handle
 * @returns {boolean | undefined}
 */
function readCache(handle) {
  if (!handle || !badgeCache.has(handle)) return undefined;
  const value = badgeCache.get(handle);
  badgeCache.delete(handle);
  badgeCache.set(handle, value);
  return value;
}

/**
 * @param {string | null} handle
 * @param {boolean} value
 * @returns {boolean}
 */
function writeCache(handle, value) {
  if (!handle) return value;
  badgeCache.delete(handle);
  badgeCache.set(handle, value);
  while (badgeCache.size > CACHE_LIMIT) {
    badgeCache.delete(badgeCache.keys().next().value);
  }
  return value;
}

/**
 * @param {Element} card
 * @param {string | null} handle
 * @returns {boolean}
 */
function shouldAddBadge(card, handle) {
  const cached = readCache(handle);
  if (cached !== undefined) return cached;

  const json = card.querySelector('script[data-product-json]');
  if (!json) return writeCache(handle, false);

  try {
    const product = JSON.parse(json.textContent || '{}');
    return writeCache(handle, Boolean(product.tags?.includes(TAG)));
  } catch {
    return writeCache(handle, false);
  }
}

/**
 * @param {ParentNode} [root]
 */
export function injectNostoPlpBadges(root = document) {
  const cards = root.querySelectorAll('product-card[data-product-handle], .product-card[data-product-handle]');
  cards.forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    if (card.querySelector('.nosto-content-badge')) return;

    const handle = card.getAttribute('data-product-handle');
    if (!shouldAddBadge(card, handle)) return;

    const media = card.querySelector('.product-card__media');
    if (!(media instanceof HTMLElement)) return;

    const badge = document.createElement('img');
    badge.src = BADGE_URL;
    badge.className = 'nosto-content-badge';
    badge.alt = '';
    badge.decoding = 'async';
    badge.loading = 'lazy';
    media.appendChild(badge);
  });
}

function scheduleInject() {
  window.clearTimeout(injectTimer);
  injectTimer = window.setTimeout(() => injectNostoPlpBadges(), 50);
}

function observePlp() {
  const roots = document.querySelectorAll('.bt-plp, [data-bt-plp], main');
  roots.forEach((el) => {
    if (!(el instanceof HTMLElement) || el.dataset.nostoBadgeObserved === 'true') return;
    el.dataset.nostoBadgeObserved = 'true';

    const observer = new MutationObserver(() => scheduleInject());
    observer.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-busy', 'hidden'] });
  });
}

function start() {
  injectNostoPlpBadges();
  observePlp();
  // Late Nosto / section hydration
  window.setTimeout(() => {
    injectNostoPlpBadges();
    observePlp();
  }, 500);
  window.setTimeout(injectNostoPlpBadges, 1500);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}

document.addEventListener('shopify:section:load', () => {
  injectNostoPlpBadges();
  observePlp();
});
